import { type FC, useCallback, useContext, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Stack, styled, Typography, useTheme } from "@mui/material";
import { DataGridPro, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid-pro";

import { type Batch } from "@/schemas/routing/batch";
import { useCanManageJobs } from "@/hooks/use-can-manage-jobs";
import { AvailableBatchesActionMenu } from "@/views/production/orders/available-batches/";
import { OrdersContext } from "@/views/production/orders/orders.context";
import { formatOptimalZFoldNames, getJobContentsString } from "@/views/production/orders/utils/utils";

import { AvailableBatchStatus } from "./available-batch-status";

const BodyOverflow = styled(Typography)`
	overflow: hidden;
	text-overflow: ellipsis;
`;

interface AvailableBatchesTableProps {
	readonly batches: Batch[];
	readonly pageSize: number;
	readonly setPageSize: (pageSize: number) => void;
	readonly pageNumber: number;
	readonly setPageNumber: (pageNumber: number) => void;
	readonly isLoading: boolean;
	readonly totalResults: number;
	readonly filter: string | null;
}

const NoRowsOverlay: FC<{ readonly filter: string | null }> = ({ filter }) => {
	const hasFilter = Boolean(filter && filter.trim() !== "");

	return (
		<Stack sx={{ alignItems: "center", justifyContent: "center", height: "100%" }}>
			<Typography variant="h4" color="textDisabled">
				{hasFilter === true && (
					<FormattedMessage
						id="There are no records that match your filter"
						defaultMessage="There are no records that match your filter"
					/>
				)}
				{hasFilter === false && (
					<FormattedMessage id="There are no records to display" defaultMessage="There are no records to display" />
				)}
			</Typography>
		</Stack>
	);
};

export const AvailableBatchesTable: FC<AvailableBatchesTableProps> = ({
	batches,
	pageSize,
	pageNumber,
	setPageNumber,
	setPageSize,
	isLoading,
	totalResults,
	filter,
}) => {
	const intl = useIntl();
	const theme = useTheme();
	const userCanRemove = useCanManageJobs();
	const { scanToTriggerConfigured } = useContext(OrdersContext);
	const doesAnyBatchHaveAPackStation = batches.some((b) => b.packStation);
	const doAnyBatchesHaveBlockedReason = batches.some((b) => b.blockedReason);

	const paginationModel: GridPaginationModel = useMemo(
		() => ({
			page: pageNumber - 1,
			pageSize,
		}),
		[pageSize, pageNumber],
	);

	const handlePaginationModelChange = (newModel: GridPaginationModel) => {
		// MUI DataGrid pages are 0-indexed, but setPageNumber expects 1-indexed
		setPageNumber(newModel.page + 1);
		setPageSize(newModel.pageSize);
	};

	const columns: GridColDef<Batch>[] = useMemo(() => {
		const cols: GridColDef<Batch>[] = [
			{
				field: "alias",
				headerName: intl.formatMessage({ id: "Title", defaultMessage: "Title" }),
				flex: 1.5,
				sortable: false,
				disableColumnMenu: true,
				renderCell: (params) => <BodyOverflow variant="body2">{params.row.alias}</BodyOverflow>,
			},
		];

		if (scanToTriggerConfigured) {
			cols.push({
				field: "licensePlateNumber",
				headerName: intl.formatMessage({ id: "Trigger Value", defaultMessage: "Trigger Value" }),
				flex: 2,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (_value, row) => row.licensePlateNumber || "",
				renderCell: (params) => <BodyOverflow variant="body2">{params.row.licensePlateNumber}</BodyOverflow>,
			});
		}

		cols.push(
			{
				field: "contents",
				headerName: intl.formatMessage({ id: "Contents", defaultMessage: "Contents" }),
				flex: 1.5,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (_value, row) =>
					getJobContentsString({
						cartonCountPerJob: row.cartonCountPerJob,
						labelCountPerJob: row.labelCountPerJob,
						cartonDimension: row.cartonDimension,
						intl,
					}),
				renderCell: (params) => (
					<BodyOverflow>
						{getJobContentsString({
							cartonCountPerJob: params.row.cartonCountPerJob,
							labelCountPerJob: params.row.labelCountPerJob,
							cartonDimension: params.row.cartonDimension,
							intl,
						})}
					</BodyOverflow>
				),
			},
			{
				field: "quantity",
				headerName: intl.formatMessage({ id: "Qty", defaultMessage: "Qty" }),
				flex: 0.5,
				sortable: false,
				disableColumnMenu: true,
				renderCell: (params) => <Typography variant="body2">{params.row.quantity}</Typography>,
			},
		);

		if (doesAnyBatchHaveAPackStation) {
			cols.push({
				field: "packStation",
				headerName: intl.formatMessage({ id: "PS", defaultMessage: "PS" }),
				flex: 0.25,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (_value, row) => row.packStation || "",
				renderCell: (params) => <Typography variant="body2">{params.row.packStation}</Typography>,
			});
		}

		cols.push(
			{
				field: "optimalZFoldNames",
				headerName: intl.formatMessage({ id: "Optimal z-Fold", defaultMessage: "Optimal z-Fold" }),
				flex: 1,
				sortable: false,
				disableColumnMenu: true,
				valueGetter: (_value, row) => formatOptimalZFoldNames({ optimalZFoldNames: row.optimalZFoldNames, intl }),
				renderCell: (params) => (
					<BodyOverflow variant="body2">
						{formatOptimalZFoldNames({ optimalZFoldNames: params.row.optimalZFoldNames, intl })}
					</BodyOverflow>
				),
			},
			{
				field: "status",
				headerName: doAnyBatchesHaveBlockedReason ? intl.formatMessage({ id: "Status", defaultMessage: "Status" }) : "",
				flex: 1,
				sortable: false,
				disableColumnMenu: true,
				align: "center",

				renderCell: (params) => <AvailableBatchStatus batch={params.row} />,
			},
		);

		if (userCanRemove) {
			cols.push({
				field: "actions",
				headerName: "",
				sortable: false,
				disableColumnMenu: true,
				flex: 0.4,
				align: "center",
				renderCell: (params) => <AvailableBatchesActionMenu batch={params.row} />,
			});
		}

		return cols;
	}, [intl, scanToTriggerConfigured, doesAnyBatchHaveAPackStation, doAnyBatchesHaveBlockedReason, userCanRemove]);

	const noRowsOverlay = useCallback(() => <NoRowsOverlay filter={filter} />, [filter]);

	return (
		<DataGridPro
			rows={batches}
			columns={columns}
			getRowId={(row) => row.batchId}
			loading={isLoading}
			pagination
			paginationMode="server"
			paginationModel={paginationModel}
			onPaginationModelChange={handlePaginationModelChange}
			rowCount={totalResults}
			pageSizeOptions={[5, 10, 15]}
			hideFooterSelectedRowCount
			disableRowSelectionOnClick
			disableColumnResize
			disableColumnReorder
			slots={{
				noRowsOverlay,
			}}
			sx={{
				flexGrow: 1,
				borderLeft: 0,
				borderRight: 0,
				borderBottom: 0,
				borderTopLeftRadius: 0,
				borderTopRightRadius: 0,
				minHeight: 320,
				"& .MuiTablePagination-root": {
					borderTop: 0,
				},
				"& .MuiDataGrid-columnSeparator": {
					opacity: "1 !important",
				},
				"& .MuiDataGrid-columnHeader": {
					paddingLeft: 2,
				},
				"& .MuiDataGrid-row:nth-of-type(odd)": {
					backgroundColor: theme.palette.grey[100],
				},
				"& .MuiDataGrid-row:nth-of-type(even)": {
					backgroundColor: theme.palette.common.white,
				},
				"& .MuiDataGrid-cell": {
					paddingLeft: 2,
					alignContent: "center",
					justifyContent: "center",
				},
			}}
		/>
	);
};
